import React from 'react'
import { Alert, Button, Icon, Spin } from 'antd'
import NewHeader from '../../../../components/NewHeader'
import BrandChoicePanel from './components/BrandChoicePanel'
import BrandChat from './components/BrandChat'
import BrandProfilePanel from './components/BrandProfilePanel'
import { useBrandOnboarding } from './hooks'
import './BrandOnboarding.scss'

const BrandOnboarding = ({
  organizationId,
  initialSessionId,
  shouldCreateSession,
  onComplete,
  onGoToVideoList
}) => {
  const onboarding = useBrandOnboarding({
    organizationId,
    initialSessionId,
    shouldCreateSession,
    onComplete
  })

  const {
    sessionId,
    viewMode,
    messages,
    uploadedFiles,
    metadata,
    brandProfile,
    brandDocumentUrl,
    brandImageUrl,
    isCreatingSession,
    isStreaming,
    isFinalizing,
    isDownloadingBrandDocument,
    error,
    canFinalize,
    acceptedExtensions,
    startFromScratch,
    uploadGuidelines,
    sendMessage,
    returnToSetupOptions,
    editBrandProfile,
    viewBrandProfile,
    downloadBrandDocument,
    finalizeProfile,
    continueToVideo,
    retryCreateSession
  } = onboarding

  const renderHeader = ({ onBack } = {}) => (
    <header className="brand-onboarding__header">
      <div className="brand-onboarding__header-main">
        {onBack ? (
          <Button
            className="brand-onboarding__header-back"
            onClick={onBack}
            title="Back to setup"
          >
            <Icon type="left" />
          </Button>
        ) : null}
        <div className="brand-onboarding__mark">
          <Icon type="skin" />
        </div>
        <div>
          <div className="brand-onboarding__kicker">
            Organization brand setup
          </div>
          <h1>AI Video Brand Onboarding</h1>
          <p>
            Build the brand profile that guides video style, voice, imagery,
            music, and defaults.
          </p>
        </div>
      </div>
      <div className="brand-onboarding__header-right">
        <NewHeader />
      </div>
    </header>
  )

  const renderBody = () => {
    if (isCreatingSession) {
      return (
        <div className="brand-onboarding__loading">
          <Spin size="large" />
          <div>
            <h2>Preparing brand onboarding</h2>
            <p>Starting a guided setup session for this organization.</p>
          </div>
        </div>
      )
    }

    if (!sessionId && error) {
      return (
        <div className="brand-onboarding__empty">
          <Icon type="warning" />
          <h2>Brand onboarding could not start</h2>
          <p>{error}</p>
          <div className="brand-onboarding__empty-actions">
            <Button type="primary" onClick={retryCreateSession}>
              Try again
            </Button>
            <Button icon="unordered-list" onClick={onGoToVideoList}>
              Go to video list
            </Button>
          </div>
        </div>
      )
    }

    if (viewMode === 'summary') {
      return (
        <BrandProfilePanel
          brandProfile={brandProfile || {}}
          brandImageUrl={brandImageUrl}
          brandDocumentUrl={brandDocumentUrl}
          headerContent={renderHeader({ onBack: returnToSetupOptions })}
          onEditProfile={editBrandProfile}
          onDownloadDocument={downloadBrandDocument}
          isDownloadingDocument={isDownloadingBrandDocument}
          onContinue={continueToVideo}
        />
      )
    }

    if (viewMode === 'chat') {
      return (
        <BrandChat
          messages={messages}
          metadata={metadata}
          uploadedFiles={uploadedFiles}
          isStreaming={isStreaming}
          isFinalizing={isFinalizing}
          canFinalize={canFinalize}
          onBackToSetup={returnToSetupOptions}
          onSendMessage={sendMessage}
          onFinalize={finalizeProfile}
        />
      )
    }

    return (
      <BrandChoicePanel
        disabled={isStreaming || isFinalizing}
        acceptedExtensions={acceptedExtensions}
        hasCompletedProfile={Boolean(brandProfile)}
        onUploadGuidelines={uploadGuidelines}
        onStartFromScratch={startFromScratch}
        onViewProfile={viewBrandProfile}
        onGoToVideoList={onGoToVideoList || continueToVideo}
      />
    )
  }

  return (
    <div
      className={`brand-onboarding${
        viewMode === 'summary' ? ' brand-onboarding--summary' : ''
      }`}
    >
      {viewMode === 'summary' ? null : renderHeader()}

      {error && sessionId ? (
        <Alert
          className="brand-onboarding__alert"
          type="error"
          showIcon
          message={error}
        />
      ) : null}

      <main className="brand-onboarding__body">{renderBody()}</main>
    </div>
  )
}

export default BrandOnboarding
